import { create } from 'zustand';
import { _SMALL_SCREEN_WIDTH, _freeCards, _parentMode } from '../constants';
import { subscribeWithSelector } from 'zustand/middleware';
import { useUserStore } from './userZustand';
import { useTreeStore } from './treeZustand';

let lastWindowWidth = window.innerWidth;

type SelectedLabels = {
    [label: string]: number[];
  };
  
function getSelectedLabelsKeys(selectedLabels: SelectedLabels): string[] {
    if(selectedLabels === undefined){
        return [];
    }
    if(Object.keys(selectedLabels).length === 0){
        return [];
    }
    // Get the maximum position to determine the length of the result array
    const maxPosition = Math.max(...Object.values(selectedLabels).flat());
  
    // Initialize the result array with undefined values
    const resultArray: (string | undefined)[] = Array(maxPosition + 1).fill(undefined);
  
    // Iterate through the selectedLabels and fill in the result array
    Object.entries(selectedLabels).forEach(([label, positions]) => {
      positions.forEach((position) => {
        resultArray[position] = label;
      });
    });
  
    // Filter out any undefined values (positions without corresponding labels)
    const result: string[] = resultArray.filter((label) => label !== undefined) as string[];
  
    return result;
}

interface UIState {
    /**
     * Boolean that are by default undefined
     * need distinction between "initial state"
     * and "false" because they are really false
     */

    isArtistMode: boolean | undefined;
    setIsArtistMode: (isArtistMode: boolean) => void;

    isMenuOpen: boolean | undefined;
    setIsMenuOpen: (isMenuOpen: boolean) => void;

    isSetNewCodeDialogOpen: boolean;
    setIsSetNewCodeDialogOpen: (isSetNewCodeDialogOpen: boolean) => void;

    isEnterCodeDialogOpen: boolean;
    setIsEnterCodeDialogOpen: (isEnterCodeDialogOpen: boolean) => void;
    
    newLabelsDialogKind: string; // "add" or "edit"
    setNewLabelsDialogKind: (newLabelsDialogKind: string) => void;

    isChoiceDialogOpen: boolean;
    setIsChoiceDialogOpen: (isChoiceDialogOpen: boolean) => void;

    selectedLabelsUpdatedToggle: boolean;
    setSelectedLabelsUpdatedToggle: (selectedLabelsUpdatedToggle: boolean) => void;
    selectedLabels: {[key: string] : number[]} ;
    selectedLabelsKeys: string[];
    addSelectedLabels: (label:string, position:number) => void;
    addAllToSelectedLabels: (labels:string[]) => void;
    removeSelectedLabels: (label:string, position:number) => void;
    resetSelectedLabels: () => void;
    setSelectedLabels: (selectedLabels: {[key: string] : number[]}) => void;
    positionOfClickedCards: number[];
    
    isAddOrReplaceDialogOpen: boolean;
    setIsAddOrReplaceDialogOpen: (isAddOrReplaceDialogOpen: boolean) => void;

    isFreeQuotaWarningMessageOpen: boolean;
    setIsFreeQuotaWarningMessageOpen: (isFreeQuotaWarningMessageOpen: boolean) => void;
 
    windowSize: { width: number, height: number };
    handleWindowResize: () => void;
  
    isSidebarVisible: boolean; 
    setIsSidebarVisible: (isSidebarVisible: boolean) => void;
 
    isShareModalOpen: boolean;
    setIsShareModalOpen: (isShareModalOpen: boolean) => void;

    isPasteConfirmOpen: boolean;
    setIsPasteConfirmOpen: (isPasteConfirmOpen: boolean) => void;

    isGridMode: boolean;
    setIsGridMode: (isGridMode: boolean) => void;

    videoURL: string | undefined;
    setVideoURL: (videoURL: string | undefined) => void;

    isQModalOpen: boolean;
    setIsQModalOpen: (isQModalOpen: boolean) => void;

    IRLabel: string | null;
    setIRLabel: (IRLabel: string | null) => void;

    isIROpen: boolean;
    setIsIROpen: (isIROpen: boolean) => void;

    IRLabelIndex: number | null;
    setIRLabelIndex: (IRLabelIndex: number | null) => void;

    isSidebarHorizontal: boolean;
    setIsSidebarHorizontal: (isSidebarHorizontal: boolean) => void;

 
};


export const useUIStore = create<UIState>()(
    subscribeWithSelector(
        (set,get) => ({
        isArtistMode: false,
        setIsArtistMode: (isArtistMode) => set({ isArtistMode: isArtistMode }),
        isMenuOpen: false,
        isSetNewCodeDialogOpen: false,
        isEnterCodeDialogOpen: false,
        newLabelsDialogKind: "add", 
        isChoiceDialogOpen: false,
        selectedLabels: {},
        selectedLabelsKeys: [],
        videoURL: undefined,
        setVideoURL: (videoURL) => set({ videoURL: videoURL }),

        isSidebarHorizontal: false,
        setIsSidebarHorizontal: (isSidebarHorizontal) => set({ isSidebarHorizontal: isSidebarHorizontal }),

        positionOfClickedCards: [],
        isAddOrReplaceDialogOpen: false,
        selectedLabelsUpdatedToggle: false,
        
        setSelectedLabelsUpdatedToggle: (selectedLabelsUpdatedToggle) => set({ selectedLabelsUpdatedToggle: selectedLabelsUpdatedToggle }),
        setSelectedLabels: (selectedLabels) => {

            set({ selectedLabels: selectedLabels ,
                selectedLabelsKeys: getSelectedLabelsKeys(selectedLabels),
                  selectedLabelsUpdatedToggle: !useUIStore.getState().selectedLabelsUpdatedToggle });
        },

        IRLabel: null,
        setIRLabel: (IRLabel) => set({ IRLabel: IRLabel }),

        isIROpen: false,
        setIsIROpen: (isIROpen) => set({ isIROpen: isIROpen }),

        IRLabelIndex: null,
        setIRLabelIndex: (IRLabelIndex) => set({ IRLabelIndex: IRLabelIndex }),

        isFreeQuotaWarningMessageOpen: false,
        setIsFreeQuotaWarningMessageOpen: (isFreeQuotaWarningMessageOpen) => set({ isFreeQuotaWarningMessageOpen: isFreeQuotaWarningMessageOpen }),

        windowSize: { width: window.innerWidth, height: window.innerHeight },
        handleWindowResize: () => {
            if (Math.abs(window.innerWidth - lastWindowWidth) > 100) {
              set({
                isSidebarVisible: window.innerWidth > _SMALL_SCREEN_WIDTH,
                windowSize: {
                  width: window.innerWidth,
                  height: window.innerHeight,
                },
              });        
            }
          },

        isSidebarVisible: window.innerWidth > _SMALL_SCREEN_WIDTH,
        setIsSidebarVisible: (isSidebarVisible) => set({ isSidebarVisible: isSidebarVisible }),

        isQModalOpen: false,
        setIsQModalOpen: (isQModalOpen) => set({ isQModalOpen: isQModalOpen }),

        isShareModalOpen: false,
        setIsShareModalOpen: (isShareModalOpen) => set({ isShareModalOpen: isShareModalOpen }),

        isPasteConfirmOpen: false,
        setIsPasteConfirmOpen: (isPasteConfirmOpen) => set({ isPasteConfirmOpen: isPasteConfirmOpen }),
        
        setIsMenuOpen: (isMenuOpen) => set({ isMenuOpen: isMenuOpen }),
        setIsSetNewCodeDialogOpen: (isSetNewCodeDialogOpen) => set({ isSetNewCodeDialogOpen: isSetNewCodeDialogOpen }),
        setIsEnterCodeDialogOpen: (isEnterCodeDialogOpen) => set({ isEnterCodeDialogOpen: isEnterCodeDialogOpen }),
        setNewLabelsDialogKind: (newLabelsDialogKind) => set({ newLabelsDialogKind: newLabelsDialogKind }),
        setIsChoiceDialogOpen: (isChoiceDialogOpen) => set({ isChoiceDialogOpen: isChoiceDialogOpen}),
        addSelectedLabels: (label:string, position:number) => {
            // If not existing, add label to selectedLabels dict and set position as unique element
            // If existing, add the position to the array of positions if not already there:
            const selectedLabels = useUIStore.getState().selectedLabels;
            if (selectedLabels[label] === undefined){
                selectedLabels[label] = [position];
            }
            else if (!selectedLabels[label].includes(position)){
                selectedLabels[label] = [...selectedLabels[label], position];
            }
             
            // add position to positionOfClickedCards
            const positionOfClickedCards = useUIStore.getState().positionOfClickedCards;
            if (!positionOfClickedCards.includes(position)){
                positionOfClickedCards.push(position);
            }

            

            set({ selectedLabels: selectedLabels,
                  selectedLabelsUpdatedToggle: !useUIStore.getState().selectedLabelsUpdatedToggle,
                  positionOfClickedCards: positionOfClickedCards,
                  selectedLabelsKeys: getSelectedLabelsKeys(selectedLabels)
                });
        },
        addAllToSelectedLabels: (labels:string[]) => {
            // USE THIS ONLY IF labels CORRESPONDS TO ALL CURRENT LABELS
            // If not existing, add label to selectedLabels dict and set position as unique element
            // If existing, add the position to the array of positions if not already there:
            const selectedLabels = useUIStore.getState().selectedLabels;
            for (let i = 0; i < labels.length; i++){
                const label = labels[i];
                const position = i;
                if (selectedLabels[label] === undefined){
                    selectedLabels[label] = [position];
                }
                else if (!selectedLabels[label].includes(position)){
                    selectedLabels[label] = [...selectedLabels[label], position];
                }
            }
             
            // add position to positionOfClickedCards
            const positionOfClickedCards = useUIStore.getState().positionOfClickedCards;
            for (let i = 0; i < labels.length; i++){
                if (!positionOfClickedCards.includes(i)){
                    positionOfClickedCards.push(i);
                }
            }

            set({ selectedLabels: selectedLabels,
                  selectedLabelsUpdatedToggle: !useUIStore.getState().selectedLabelsUpdatedToggle,
                  positionOfClickedCards: positionOfClickedCards,
                  selectedLabelsKeys: getSelectedLabelsKeys(selectedLabels)

                });
            
        },
        removeSelectedLabels: (label:string, position:number) => {
            const selectedLabels = useUIStore.getState().selectedLabels;
            if (selectedLabels[label] !== undefined) {
                // Remove the current position
                selectedLabels[label] = selectedLabels[label].filter(pos => pos !== position);
        
                // If the array of positions is empty, remove the label from the dict
                if (selectedLabels[label].length === 0) {
                    delete selectedLabels[label];
                }
        
                // Remove position from positionOfClickedCards
                const positionOfClickedCards = useUIStore.getState().positionOfClickedCards;
                const positionIndex = positionOfClickedCards.indexOf(position);
                if (positionIndex !== -1) {
                    positionOfClickedCards.splice(positionIndex, 1);
                }
        
                set({
                    selectedLabels: selectedLabels,
                    selectedLabelsUpdatedToggle: !useUIStore.getState().selectedLabelsUpdatedToggle,
                    positionOfClickedCards: positionOfClickedCards,
                    selectedLabelsKeys: getSelectedLabelsKeys(selectedLabels)
                });
            }
        },

        resetSelectedLabels: () => {set({
            selectedLabels: {},
            positionOfClickedCards: [],
            selectedLabelsUpdatedToggle: !useUIStore.getState().selectedLabelsUpdatedToggle,
            selectedLabelsKeys: []
         })},
         
        setIsAddOrReplaceDialogOpen: (isAddOrReplaceDialogOpen) => set({ isAddOrReplaceDialogOpen: isAddOrReplaceDialogOpen }),

        isGridMode: false,
        setIsGridMode: (isGridMode) => set({ isGridMode: isGridMode })
        

    })
    )
);

//Caused problems on mobile devices because of the resize event
//being triggered by the keyboard opening which led to dismisal of popovers and modals
//window.addEventListener('resize', useUIStore.getState().handleWindowResize)
//It would be enough to check if the windows size changed by a given minimum amount in the handleWindowResize function
//But I prefer to only add event listener if not mobile devices,
//because mobile devices are unlikely to be "resized"! So why bother...
if (!window.matchMedia('only screen and (max-width: 760px)').matches) {
    window.addEventListener('resize', useUIStore.getState().handleWindowResize);
}

// Subscribe to userStore in order to check subscription status and change
// isFreeQuotaWarningMessageOpen accordingly
// SUBSCRIPTION TO MULTIPLE STORES (userStore and treeStore)
const unsubUserStore = useUserStore.subscribe(
    (state) => ({ isSubscribed: state.isSubscribed, nodesCount: useTreeStore.getState().nodesCount }),
    (state) => {
        if(state.isSubscribed === false && state.nodesCount > _freeCards){
            useUIStore.getState().setIsFreeQuotaWarningMessageOpen(true);
        }else{
            useUIStore.getState().setIsFreeQuotaWarningMessageOpen(false);
        }
    }
);

